/**
 * WHERE YOU WERE, for after you sign in again.
 *
 * A session that runs out mid-use already comes back to the exact page: the
 * bounce to the house login carries it as `?next=`. A FRESH sign-in did not -
 * after logging out, opening the login page yourself, or coming back another
 * day, you landed on your last product's front page and had to find your way
 * back to the room, the view and the filter you had open.
 *
 * So each product site remembers its own last page in its own localStorage,
 * tagged with whose it is, and the house login sends a fresh sign-in to that
 * product with `?resume=1`. The product, seeing the mark, goes to the page it
 * remembered. localStorage is per site, which is why the product does the
 * looking: the login page lives on another host and could not read it.
 *
 * ONLY YOUR OWN SPOT. A shared computer's last page belongs to whoever was
 * signed in; another account that signs in after them lands on the front
 * page, not in someone else's room. And a spot older than a month is dropped:
 * by then the front page is the better place to start.
 *
 * Pure apart from the storage handed in, so jest can pin it.
 */

export const LAST_SPOT_KEY = 'jcseo_last_spot';
export const RESUME_PARAM = 'resume';
export const SPOT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface Spot {
  user: number;
  path: string;
  at: number;
}

type Store = Pick<Storage, 'getItem' | 'setItem'>;

/** This site's localStorage, or a store that keeps nothing when the browser
 *  refuses it (blocked site data reads as an error, not as empty). */
export function browserStore(): Store {
  try {
    return window.localStorage;
  } catch {
    return { getItem: () => null, setItem: () => undefined };
  }
}

/** A path with the resume mark on it: `/` -> `/?resume=1`. */
export function withResume(path: string): string {
  const [head, hash = ''] = path.split('#');
  const [pathname, query = ''] = head.split('?');
  const q = new URLSearchParams(query);
  q.set(RESUME_PARAM, '1');
  return `${pathname || '/'}?${q.toString()}${hash ? `#${hash}` : ''}`;
}

/** The search string without the mark, `?` included when anything is left. */
export function withoutResume(search: string): string {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  q.delete(RESUME_PARAM);
  const rest = q.toString();
  return rest ? `?${rest}` : '';
}

export function hasResume(search: string): boolean {
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(RESUME_PARAM) === '1';
}

/** Remember this page for this person. Never throws: a full or blocked store
 *  only means there is nothing to come back to. */
export function saveSpot(store: Store, user: number, path: string, now: number = Date.now()): void {
  try {
    store.setItem(LAST_SPOT_KEY, JSON.stringify({ user, path, at: now } as Spot));
  } catch {
    /* nothing to come back to */
  }
}

/** The page to go back to, for this person, or null. */
export function spotFor(store: Store, user: number, now: number = Date.now()): string | null {
  try {
    const raw = store.getItem(LAST_SPOT_KEY);
    if (!raw) return null;
    const spot = JSON.parse(raw) as Partial<Spot>;
    if (spot.user !== user || typeof spot.path !== 'string' || typeof spot.at !== 'number') return null;
    if (now - spot.at > SPOT_MAX_AGE_MS) return null;
    // Our own pages only: a path, never a full address to somewhere else.
    if (!spot.path.startsWith('/') || spot.path.startsWith('//')) return null;
    return spot.path;
  } catch {
    return null;
  }
}
